// src/pages/clients/ClientConnectionMap.js
import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Paper,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    CircularProgress,
    Chip,
    Button
} from '@mui/material';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIconShadow from 'leaflet/dist/images/marker-shadow.png';
import {
    NetworkWifi as WifiIcon,
    Map as MapIcon,
    MyLocation as LocationIcon,
    Directions as DirectionsIcon
} from '@mui/icons-material';

// Corregir el problema de los iconos en Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl: markerIcon,
    shadowUrl: markerIconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

// Iconos personalizados
const clientIcon = L.divIcon({
    className: 'custom-div-icon',
    html: `
        <div style="position: relative; width: 22px; height: 35px;">
            <div style="position: absolute; top: 0; left: 1px; width: 20px; height: 20px; 
                background: radial-gradient(circle at 40% 30%,rgb(91, 240, 86) 0%,rgb(38, 190, 71) 60%,rgb(12, 122, 30) 100%); 
                border-radius: 50%; 
                box-shadow: 0 2px 5px rgba(0,0,0,0.3),
                            inset 0 -2px 5px rgba(0,0,0,0.1),
                            inset 0 2px 3px rgba(255,255,255,0.3);"></div>
            <div style="position: absolute; top: 15px; left: 10px; width: 2px; height: 20px; 
                background: linear-gradient(to right, rgba(150, 148, 148, 0.9), white, rgba(92, 91, 91, 0.9)); 
                box-shadow: 0 2px 2px rgba(0,0,0,0.1);"></div>
        </div>
    `,
    iconSize: [22, 35],
    iconAnchor: [11, 35],
    popupAnchor: [0, -38]
});

const inactiveClientIcon = L.divIcon({
    className: 'custom-div-icon',
    html: `<div style="background-color: #F44336; width: 10px; height: 10px; border-radius: 50%; border: 2px solid white;"></div>`,
    iconSize: [15, 15],
    iconAnchor: [7, 7]
});

const apIcon = L.divIcon({
    className: 'custom-div-icon',
    html: `
        <div style="position: relative; width: 30px; height: 40px;">
            <!-- Pin base shape -->
            <div style="position: absolute; top: 0; left: 5px; width: 20px; height: 20px; 
                background-color: #001f5c; 
                border-radius: 50% 50% 50% 0; 
                transform: rotate(-45deg);"></div>
                
            <!-- Extended pin tail -->
            <div style="position: absolute; top: 14px; left: 0; width: 30px; height: 25px;
                clip-path: polygon(50% 100%, 25% 0, 75% 0);
                background-color: #001f5c;"></div>
                
            <!-- White circular ring -->
            <div style="position: absolute; top: 5px; left: 10px; width: 10px; height: 10px; 
                border: 2px solid white; 
                border-radius: 50%;
                background-color: transparent;"></div>
        </div>
    `,
    iconSize: [30, 40],
    iconAnchor: [15, 40], // Punto de anclaje en la base del pin
    popupAnchor: [0, -42] // Punto desde donde se abrirá el popup
});

// Función para formatear la distancia
function formatDistance(distance) {
    if (distance === null || distance === undefined) return "Desconocida";
    if (distance < 1) {
        return `${(distance * 1000).toFixed(0)} m`;
    }
    return `${distance} km`;
}

// API base URL - centralized for easier management
const API_BASE_URL = '';

// Componente principal
const ClientConnectionMap = () => {
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const clientsLayerRef = useRef(null);
    const apsLayerRef = useRef(null);
    const connectionsLayerRef = useRef(null);
    const mapInitializedRef = useRef(false);

    const [zones, setZones] = useState([]);
    const [selectedZone, setSelectedZone] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState({
        clients: 0,
        accessPoints: 0,
        connections: 0,
        avgDistance: 0
    });
    const [selectedItem, setSelectedItem] = useState(null);

    // Utility function for API calls
    const callApi = async (endpoint, options = {}) => {
        const token = localStorage.getItem('token');
        if (!token) {
            throw new Error('No authentication token found. Please login again.');
        }

        const url = `${API_BASE_URL}${endpoint}`;
        const headers = {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            ...options.headers
        };

        try {
            const response = await fetch(url, {
                ...options,
                headers
            });

            if (!response.ok) {
                // Try to get error details from response
                let errorMessage = `Error ${response.status}: ${response.statusText}`;
                try {
                    // Check response content type to avoid parsing non-JSON as JSON
                    const contentType = response.headers.get('content-type');
                    if (contentType && contentType.includes('application/json')) {
                        const errorData = await response.json();
                        errorMessage = errorData.message || errorMessage;
                    } else {
                        // If not JSON, just get text
                        const errorText = await response.text();
                        console.error('Non-JSON error response:', errorText);
                    }
                } catch (parseError) {
                    console.error('Error parsing error response:', parseError);
                }

                // Handle authentication errors
                if (response.status === 401 || response.status === 403) {
                    localStorage.removeItem('token');
                    throw new Error('Session expired. Please login again.');
                }

                throw new Error(errorMessage);
            }

            // Check if response is empty
            const responseText = await response.text();
            if (!responseText.trim()) {
                return null;
            }

            // Parse JSON
            try {
                return JSON.parse(responseText);
            } catch (parseError) {
                console.error('Failed to parse response as JSON:', responseText);
                throw new Error('Invalid JSON response from server');
            }
        } catch (error) {
            console.error(`API call to ${url} failed:`, error);
            throw error;
        }
    };

    // Cargar zonas al inicio
    useEffect(() => {
        fetchZones();
    }, []);

    // Inicializar mapa solo una vez
    useEffect(() => {
        if (!mapRef.current || mapInitializedRef.current) return;

        initializeMap();

        // Limpiar al desmontar
        return () => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
                mapInitializedRef.current = false;
            }
        };
    }, []);

    // Cargar datos cuando cambian los filtros
    useEffect(() => {
        if (mapInstanceRef.current && clientsLayerRef.current) {
            loadData();
        }
    }, [selectedZone, statusFilter]);

    const fetchZones = async () => {
        try {
            const data = await callApi('/api/zones');

            if (Array.isArray(data)) {
                setZones(data);
            } else {
                console.error('La respuesta de /api/zones no es un array:', data);
                setZones([]);
            }
        } catch (error) {
            console.error('Error fetching zones:', error);
            setError(`Error al cargar zonas: ${error.message}`);
            setZones([]);
        }
    };

    const initializeMap = () => {
        if (!mapRef.current || mapInitializedRef.current) return;

        try {
            // Definir las capas base
            const openStreetMapLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
                maxZoom: 22,
                maxNativeZoom: 19
            });

            const googleHybrid = L.tileLayer('https://{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}', {
                subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
                attribution: '&copy; Google Maps',
                maxZoom: 22,
                maxNativeZoom: 20
            });

            // Capa de satélite ESRI (no requiere API key)
            const satelliteLayer = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
                attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
                maxZoom: 22,
                maxNativeZoom: 17
            });

            // Crear instancia de mapa con las capas definidas
            const map = L.map(mapRef.current, {
                center: [-4.9, -80.77], // Coordenadas para Perú
                zoom: 13,
                maxZoom: 22,
                layers: [googleHybrid] // Capa por defecto
            });

            // Definir capas base para el control
            const baseLayers = {
                "Mapa Estándar": openStreetMapLayer,
                "Satelital Google (Híbrido)": googleHybrid,
                "Satélite Esri": satelliteLayer
            };

            // Guardar referencias
            mapInstanceRef.current = map;
            clientsLayerRef.current = L.layerGroup().addTo(map);
            apsLayerRef.current = L.layerGroup().addTo(map);
            connectionsLayerRef.current = L.layerGroup().addTo(map);

            // Definir capas superpuestas para el control
            const overlays = {
                "Clientes": clientsLayerRef.current,
                "Puntos de Acceso": apsLayerRef.current,
                "Conexiones": connectionsLayerRef.current
            };

            // Añadir control de capas
            L.control.layers(baseLayers, overlays).addTo(map);

            // Añadir escala
            L.control.scale().addTo(map);

            // Marcar mapa como inicializado
            mapInitializedRef.current = true;

            // Cargar datos iniciales
            loadData();
        } catch (error) {
            console.error("Error initializing map:", error);
            setError("Error al inicializar el mapa: " + error.message);
            setLoading(false);
        }
    };

    const loadData = async () => {
        if (!mapInstanceRef.current ||
            !clientsLayerRef.current ||
            !apsLayerRef.current ||
            !connectionsLayerRef.current) return;

        try {
            setLoading(true);
            setError(null);
            setSelectedItem(null);

            // Limpiar capas
            clientsLayerRef.current.clearLayers();
            apsLayerRef.current.clearLayers();
            connectionsLayerRef.current.clearLayers();

            // Construir URL con filtros
            let endpoint = '/api/client-ap-connections';
            const params = new URLSearchParams();

            if (selectedZone) {
                params.append('zone', selectedZone);
            }

            if (statusFilter !== 'all') {
                params.append('status', statusFilter);
            }

            if (params.toString()) {
                endpoint += `?${params.toString()}`;
            }

            // Realizar petición usando la función centralizada
            const data = await callApi(endpoint);

            // Check for valid response structure
            if (!data) {
                throw new Error('Respuesta vacía del servidor');
            }

            // Verificar estructura de la respuesta
            if (!data.clients || !data.accessPoints) {
                console.error('Formato de respuesta inválido:', data);
                throw new Error('Formato de respuesta inválido');
            }

            // Actualizar estadísticas
            setStats(data.stats || {
                clients: data.clients.length,
                accessPoints: data.accessPoints.length,
                connections: 0,
                avgDistance: 0
            });

            // Procesar puntos de acceso (APs)
            data.accessPoints.forEach(ap => {
                if (ap.coordinates && ap.coordinates.lat && ap.coordinates.lng) {
                    const typeLabels = {
                        sectorial: 'AP Sectorial',
                        omnidireccional: 'AP Omnidireccional',
                        ptp: 'Enlace PTP (Backhaul)',
                        repetidor: 'Repetidor'
                    };
                    const apTypeLabel = typeLabels[ap.type] || ap.type || 'Punto de Acceso';

                    // Crear marcador para el AP
                    L.marker([ap.coordinates.lat, ap.coordinates.lng], {
                        icon: apIcon,
                        title: ap.name
                    })
                        .bindPopup(`
                        <div>
                            <h3>${ap.name || 'AP sin nombre'}</h3>
                            <p><strong>Tipo:</strong> ${apTypeLabel}</p>
                            <p><strong>IP:</strong> ${ap.ip || 'N/A'}</p>
                            <p><strong>Modelo:</strong> ${ap.model || 'N/A'}</p>
                            <p><strong>Clientes conectados:</strong> ${ap.client_count || 0}</p>
                        </div>
                    `)
                        .on('click', () => setSelectedItem({ type: 'ap', data: ap }))
                        .addTo(apsLayerRef.current);
                }
            });

            // Procesar clientes
            data.clients.forEach(client => {
                if (client.coordinates && client.coordinates.lat && client.coordinates.lng) {
                    // Crear marcador para el cliente
                    L.marker([client.coordinates.lat, client.coordinates.lng], {
                        icon: client.status === 'active' ? clientIcon : inactiveClientIcon,
                        title: client.fullName || client.queueName
                    })
                        .bindPopup(createClientPopup(client))
                        .on('click', () => setSelectedItem({ type: 'client', data: client }))
                        .addTo(clientsLayerRef.current);

                    // Si tiene AP asignado y el AP tiene coordenadas, crear línea de conexión
                    if (client.accessPoint &&
                        client.accessPoint.coordinates &&
                        client.accessPoint.coordinates.lat &&
                        client.accessPoint.coordinates.lng) {

                        const apCoords = [
                            client.accessPoint.coordinates.lat,
                            client.accessPoint.coordinates.lng
                        ];

                        const clientCoords = [
                            client.coordinates.lat,
                            client.coordinates.lng
                        ];

                        // Crear línea
                        L.polyline([clientCoords, apCoords], {
                            color: client.status === 'active' ? '#8efa04' : '#F44336',
                            weight: 3,
                            opacity: 0.7,
                            dashArray: '5, 2'
                        })
                            .bindTooltip(`Distancia: ${formatDistance(client.distance)}`)
                            .addTo(connectionsLayerRef.current);
                    }
                }
            });

            // Ajustar vista del mapa
            const clientMarkers = clientsLayerRef.current.getLayers();
            const apMarkers = apsLayerRef.current.getLayers();

            if (clientMarkers.length > 0 || apMarkers.length > 0) {
                const allMarkers = [...clientMarkers, ...apMarkers];
                const group = L.featureGroup(allMarkers);
                mapInstanceRef.current.fitBounds(group.getBounds(), { padding: [30, 30] });
            } else {
                setError("No se encontraron clientes o puntos de acceso con coordenadas válidas");
            }

        } catch (error) {
            console.error("Error loading data:", error);
            setError("Error al cargar datos: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    // Crear HTML para el popup del cliente
    const createClientPopup = (client) => {
        return `
            <div>
                <h3>${client.fullName || client.queueName}</h3>
                <p><strong>IP:</strong> ${client.ipAddress || 'N/A'}</p>
                <p><strong>Estado:</strong> ${client.status === 'active' ? 'Activo' : 'Inactivo'}</p>
                <p><strong>Plan:</strong> ${client.planName || 'N/A'}</p>
                <p><strong>Dirección:</strong> ${client.address || 'N/A'}</p>
                <p><strong>AP:</strong> ${client.accessPoint ? client.accessPoint.name : 'No asignado'}</p>
                ${client.distance ? `<p><strong>Distancia al AP:</strong> ${formatDistance(client.distance)}</p>` : ''}
                <button 
                    onclick="window.open('https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(client.address)}', '_blank')"
                    style="background-color: #1976d2; color: white; border: none; padding: 5px 10px; border-radius: 4px; cursor: pointer; margin-top: 8px;"
                >
                    <i class="fa fa-directions"></i> Cómo llegar
                </button>
            </div>
        `;
    };

    // Panel lateral de información detallada
    const InfoPanel = ({ item }) => {
        if (!item) return null;

        const openDirections = (address) => {
            window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`, '_blank');
        };

        return (
            <Paper elevation={3} sx={{ p: 2, height: '100%', overflow: 'auto' }}>
                {item.type === 'client' && (
                    <>
                        <Typography variant="h6" gutterBottom>
                            {item.data.fullName || item.data.queueName}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                            <Chip
                                label={item.data.status === 'active' ? 'Activo' : 'Inactivo'}
                                color={item.data.status === 'active' ? 'success' : 'error'}
                                size="small"
                            />
                        </Box>
                        <Box sx={{ mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">IP</Typography>
                            <Typography variant="body1">{item.data.ipAddress || 'N/A'}</Typography>
                        </Box>
                        <Box sx={{ mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">Plan</Typography>
                            <Typography variant="body1">{item.data.planName || 'N/A'}</Typography>
                        </Box>
                        <Box sx={{ mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">Dirección</Typography>
                            <Typography variant="body1">{item.data.address || 'N/A'}</Typography>
                        </Box>
                        <Box sx={{ mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">Zona</Typography>
                            <Typography variant="body1">{item.data.zoneName || 'N/A'}</Typography>
                        </Box>
                        <Box sx={{ mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">AP</Typography>
                            <Typography variant="body1">
                                {item.data.accessPoint ? item.data.accessPoint.name : 'No asignado'}
                            </Typography>
                        </Box>
                        {item.data.distance && (
                            <Box sx={{ mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">Distancia al AP</Typography>
                                <Typography variant="body1">{formatDistance(item.data.distance)}</Typography>
                            </Box>
                        )}
                        <Box sx={{ mt: 2 }}>
                            <Button
                                variant="contained"
                                startIcon={<DirectionsIcon />}
                                fullWidth
                                onClick={() => openDirections(item.data.address)}
                                disabled={!item.data.address}
                            >
                                Cómo llegar
                            </Button>
                        </Box>
                    </>
                )}

                {item.type === 'ap' && (
                    <>
                        <Typography variant="h6" gutterBottom>
                            {item.data.name || 'Punto de Acceso'}
                        </Typography>
                        <Box sx={{ mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">Tipo</Typography>
                            <Typography variant="body1">
                                {{
                                    sectorial: 'AP Sectorial',
                                    omnidireccional: 'AP Omnidireccional',
                                    ptp: 'Enlace PTP (Backhaul)',
                                    repetidor: 'Repetidor'
                                }[item.data.type] || item.data.type || 'Punto de Acceso'}
                            </Typography>
                        </Box>
                        <Box sx={{ mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">IP</Typography>
                            <Typography variant="body1">{item.data.ip || 'N/A'}</Typography>
                        </Box>
                        <Box sx={{ mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">Modelo</Typography>
                            <Typography variant="body1">{item.data.model || 'N/A'}</Typography>
                        </Box>
                        <Box sx={{ mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">Zona</Typography>
                            <Typography variant="body1">{item.data.zone_name || 'N/A'}</Typography>
                        </Box>
                        <Box sx={{ mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">Clientes conectados</Typography>
                            <Typography variant="body1">{item.data.client_count || 0}</Typography>
                        </Box>
                        {(item.data.node_name || item.data.address) && (
                            <Box sx={{ mt: 2 }}>
                                <Button
                                    variant="contained"
                                    startIcon={<DirectionsIcon />}
                                    fullWidth
                                    onClick={() => openDirections(item.data.node_name || item.data.address)}
                                >
                                    Cómo llegar
                                </Button>
                            </Box>
                        )}
                    </>
                )}
            </Paper>
        );
    };

    const handleZoneChange = (e) => {
        setSelectedZone(e.target.value);
    };

    const handleStatusChange = (e) => {
        setStatusFilter(e.target.value);
    };

    const handleRetry = () => {
        loadData();
    };

    return (
        <Box className="p-6">
            <Typography variant="h4" className="flex items-center mb-4">
                <MapIcon sx={{ mr: 1 }} />
                Mapa de Conexiones AP-CPE
            </Typography>

            <Box className="flex flex-wrap gap-4 mb-4">
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Estado</InputLabel>
                    <Select
                        value={statusFilter}
                        onChange={handleStatusChange}
                        label="Estado"
                    >
                        <MenuItem value="all">Todos los estados</MenuItem>
                        <MenuItem value="active">Activos</MenuItem>
                        <MenuItem value="inactive">Inactivos</MenuItem>
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Zona</InputLabel>
                    <Select
                        value={selectedZone}
                        onChange={handleZoneChange}
                        label="Zona"
                    >
                        <MenuItem value="">Todas las zonas</MenuItem>
                        {Array.isArray(zones) ?
                            zones.map(zone => (
                                <MenuItem key={zone.id} value={zone.id}>{zone.name}</MenuItem>
                            )) :
                            <MenuItem value="">Error al cargar zonas</MenuItem>
                        }
                    </Select>
                </FormControl>

                <Button
                    variant="outlined"
                    startIcon={<LocationIcon />}
                    onClick={() => {
                        if (navigator.geolocation) {
                            navigator.geolocation.getCurrentPosition(
                                (position) => {
                                    const { latitude, longitude } = position.coords;
                                    if (mapInstanceRef.current) {
                                        mapInstanceRef.current.setView([latitude, longitude], 15);
                                    }
                                },
                                (error) => {
                                    console.error('Geolocation error:', error);
                                    setError('No se pudo obtener su ubicación actual');
                                }
                            );
                        } else {
                            setError('Geolocalización no soportada en este navegador');
                        }
                    }}
                >
                    Mi ubicación
                </Button>

                <Box sx={{ flexGrow: 1 }} />

                <Box className="flex items-center gap-2">
                    <Chip
                        icon={<WifiIcon />}
                        label={`APs: ${stats.accessPoints}`}
                        color="info"
                        variant="outlined"
                    />
                    <Chip
                        label={`Clientes: ${stats.clients}`}
                        color="success"
                        variant="outlined"
                    />
                    <Chip
                        label={`Conexiones: ${stats.connections}`}
                        color="primary"
                        variant="outlined"
                    />
                    <Chip
                        label={`Distancia promedio: ${stats.avgDistance} km`}
                        color="default"
                        variant="outlined"
                    />
                </Box>
            </Box>

            {error && (
                <Alert
                    severity="error"
                    className="mb-4"
                    action={
                        <Button color="inherit" size="small" onClick={handleRetry}>
                            Reintentar
                        </Button>
                    }
                    onClose={() => setError(null)}
                >
                    {error}
                </Alert>
            )}

            <Box sx={{ display: 'flex', height: 'calc(100vh - 240px)' }}>
                <Paper
                    elevation={3}
                    sx={{
                        position: 'relative',
                        flexGrow: 1,
                        borderRadius: 2,
                        overflow: 'hidden'
                    }}
                >
                    <div
                        ref={mapRef}
                        style={{ height: '100%', width: '100%' }}
                    />

                    {loading && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                zIndex: 999
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    )}
                </Paper>

                {selectedItem && (
                    <Box sx={{ width: 300, ml: 2 }}>
                        <InfoPanel item={selectedItem} />
                    </Box>
                )}
            </Box>

            <Box className="mt-4">
                <Typography variant="caption" color="text.secondary">
                    * Las líneas punteadas muestran la conexión entre el cliente (CPE) y su punto de acceso (AP).
                    Haga clic en un cliente o AP para ver más detalles.
                </Typography>
            </Box>
        </Box>
    );
};

export default ClientConnectionMap;